/**
 * Human-readable label for ROZVADEC (distribution cabinet) objects.
 * Admin-set `name` when it differs from technical `id`; otherwise object id (no polygon/KIA/description fallback).
 */
import {
  normalizeZafukSourceRef,
  zafukSourceLabel,
  type ZafukSourceObjectType,
} from '../constants/object-workflow/zafuk-source.constants';

export interface RozvadecLabelInput {
  id?: string | null;
  name?: string | null;
}

export function formatRozvadecLabel(input: RozvadecLabelInput): string {
  const id = (input.id ?? '').trim();
  const name = (input.name ?? '').trim();
  if (name && (!id || name !== id)) return name;
  return id || name || '';
}

export interface BudovaZafukRozvadecRef {
  pripojovanyZafouknutoZRozvadecObjectId?: string | null;
  pripojovanyZafouknutoZRozvadecName?: string | null;
  zafukZRozvadecObjectId?: string | null;
  zafukZRozvadecName?: string | null;
  /** Neutral záfuk source (preferred when present). */
  zafukSourceObjectId?: string | null;
  zafukSourceObjectType?: ZafukSourceObjectType | string | null;
  zafukSourceName?: string | null;
}

/** Linked záfuk source object id on BUDOVA / záfuk rows (neutral, then FK, then legacy). */
export function resolveBudovaZafukRozvadecObjectId(input: BudovaZafukRozvadecRef): string {
  const ref = normalizeZafukSourceRef(input);
  if (ref) return ref.objectId;
  return (
    input.pripojovanyZafouknutoZRozvadecObjectId ??
    input.zafukZRozvadecObjectId ??
    ''
  ).trim();
}

/**
 * Resolve linked záfuk source label on BUDOVA / záfuk rows.
 * Prefer the cabinet (or spojkoviště) name; fall back to its object id.
 *
 * Callers pass the whole host row (WF tabulka, Objekty → Budova). That row’s own
 * `name` is the technical id of the budova — it must not replace the source name.
 */
export function formatBudovaZafukRozvadecLabel(input: BudovaZafukRozvadecRef): string {
  const ref = normalizeZafukSourceRef({
    zafukSourceObjectId: input.zafukSourceObjectId,
    zafukSourceObjectType: input.zafukSourceObjectType,
    zafukSourceName: input.zafukSourceName,
    zafukZRozvadecObjectId: input.zafukZRozvadecObjectId,
    zafukZRozvadecName: input.zafukZRozvadecName,
    pripojovanyZafouknutoZRozvadecObjectId: input.pripojovanyZafouknutoZRozvadecObjectId,
    pripojovanyZafouknutoZRozvadecName: input.pripojovanyZafouknutoZRozvadecName,
  });
  if (ref) return zafukSourceLabel(ref);
  const rozId = resolveBudovaZafukRozvadecObjectId(input);
  const storedName = (
    input.zafukSourceName ??
    input.pripojovanyZafouknutoZRozvadecName ??
    input.zafukZRozvadecName ??
    ''
  ).trim();
  const label = formatRozvadecLabel({
    id: rozId,
    name: storedName,
  });
  if (label) return label;
  return rozId;
}
